/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useInterval } from 'design-system/client';
import { useCallback, useRef, useState } from 'react';
import type { BaseUseApiHook, DemoData } from '../types';
function isGenerator<TSelection>(demoData: unknown): demoData is () => Generator<TSelection, TSelection, TSelection> {
  if (typeof demoData === 'function') return true;
  return false;
}
export function useDemoData<TSelection>(demoData: DemoData<TSelection>, intervalMs: number): {
  data: TSelection | undefined;
} {
  const [data, setData] = useState<TSelection | undefined>(undefined);
  const generatorRef = useRef<Generator<TSelection, TSelection, TSelection> | undefined>(undefined);
  const handleDemoData = useCallback(() => {
    if (!demoData) return;
    if (isGenerator(demoData)) {
      generatorRef.current ||= demoData();
      const generator = generatorRef.current;
      const result = generator.next();
      setData(result.value);
      return;
    }
    setData(demoData as TSelection);
  }, [demoData]);
  useInterval({
    callback: handleDemoData,
    executeImmediately: true,
    intervalMs,
    passAbortSignal: false
  });
  return {
    data
  };
}
function withDemoData<THook extends BaseUseApiHook>(useApi: THook) {
  return ((...args: Parameters<THook>) => {
    const [path, params, options, demoData] = args;
    const hasDemoData = demoData !== undefined;
    const {
      data: demo
    } = useDemoData(demoData, options?.intervalMs ?? 200);
    const originalSkipIf = options?.skipIf;
    const skipIf = useCallback(() => {
      return hasDemoData || (originalSkipIf?.() ?? false);
    }, [hasDemoData, originalSkipIf]);
    const {
      data,
      ...rest
    } = useApi(path, params, {
      ...options,
      skipIf
    });
    return {
      data: hasDemoData ? demo : data,
      ...rest
    };
  }) as THook;
}
export default withDemoData;